import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Typography,
  Autocomplete,
  Alert,
  Box,
  Paper,
  Divider,
  Chip,
  Avatar,
  alpha,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Tooltip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { createGrowthSession as createGrowthSessionAPI, updateGrowthSession as updateGrowthSessionAPI } from '../Queries';

const UserHierarchyDialog = ({
  open,
  onClose,
  editingHierarchy = null,
  onSuccess,
  availableUsers = [],
  selectedUserId = ''
}) => {
  const [hierarchyForm, setHierarchyForm] = useState({
    id: '',
    manager_id: '',
    lead_id: '',
    user_id: '',
    team_name: '',
    department: '',
    date: '',
    review_period_from: '',
    review_period_to: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const currentUser = localStorage.getItem("user");
  const userRole = localStorage.getItem("userRole");

  // Check if all required fields are filled
  const checkFormValidity = () => {
    const isValid = !!(
      hierarchyForm.lead_id && 
      hierarchyForm.user_id && 
      hierarchyForm.team_name && 
      hierarchyForm.team_name.trim()
    );
    return isValid;
  };

  // Get missing required fields for tooltip
  const getMissingFields = () => {
    const missing = [];
    if (!hierarchyForm.user_id) missing.push('User');
    if (!hierarchyForm.lead_id) missing.push('Lead');
    if (!hierarchyForm.team_name || !hierarchyForm.team_name.trim()) missing.push('Team Name');
    return missing;
  };

  useEffect(() => {
    if (editingHierarchy) {
      setHierarchyForm({
        id: editingHierarchy.id,
        manager_id: editingHierarchy.manager_id || '',
        lead_id: editingHierarchy.lead_id || '',
        user_id: editingHierarchy.user_id || '',
        team_name: editingHierarchy.team_name || '',
        department: editingHierarchy.department || ''
      });
    } else {
      resetForm();
    }
  }, [editingHierarchy, open, selectedUserId]);

  // Monitor form changes and update validity
  useEffect(() => {
    const isValid = checkFormValidity();
    setIsFormValid(isValid);
  }, [hierarchyForm.lead_id, hierarchyForm.user_id, hierarchyForm.team_name]);

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    const reviewEnd = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // Convert selectedUserId to email if it's an ID
    const selectedUser = selectedUserId ? availableUsers.find(user => user.id === selectedUserId || user.email === selectedUserId) : null;
    const userEmail = selectedUser ? selectedUser.email : '';

    setHierarchyForm({
      id: new Date().getTime().toString(),
      manager_id: userRole === "Manager" ? currentUser : '',
      lead_id: '',
      user_id: userEmail,
      team_name: '',
      department: '',
      date: today,
      review_period_from: today,
      review_period_to: reviewEnd
    });
    setError('');
  };

  const handleFormChange = (field, value) => {
    // For user and lead fields, extract email from the user object
    if (field === 'user_id' || field === 'lead_id') {
      setHierarchyForm(prev => ({ ...prev, [field]: value ? value.email : '' }));
    } else {
      // For other fields like team_name, department, use the value directly
      setHierarchyForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const validateForm = () => {
    if (!hierarchyForm.lead_id) {
      setError('Please select a Lead');
      return false;
    }
    if (!hierarchyForm.user_id) {
      setError('Please select a User');
      return false;
    }
    if (!hierarchyForm.team_name) {
      setError('Please enter a team name');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create a Growth Conversation entry with only lead assignment data
      const growthSessionData = {
        id: hierarchyForm.id,
        user_id: hierarchyForm.user_id,
        lead_id: hierarchyForm.lead_id,
        manager_id: hierarchyForm.manager_id || currentUser,
        session_data: {
          key_strengths: '',
          areas_for_improvement: '',
          recommended_action_plan: '',
          support_required: '',
          suggested_plan_of_action: '',
          dynamic_fields: [
            { label: 'Team Name', value: hierarchyForm.team_name, id: Date.now() },
            { label: 'Department', value: hierarchyForm.department, id: Date.now() + 1 }
          ].filter(field => field.value) // Only include fields with values
        },
        status: 'lead_assigned', // Special status to indicate this is just lead assignment
        date: '',
        review_period_from: '',
        review_period_to: '',
        manager_comments: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: currentUser
      };
       if (editingHierarchy) {
        // Update existing entry
        await updateGrowthSessionAPI(growthSessionData);
      } else {
        // Create new entry
        await createGrowthSessionAPI(growthSessionData);
      }

      onSuccess?.(editingHierarchy ? 'updated' : 'created', growthSessionData);
      onClose();
    } catch (error) {
      console.error('Error saving lead assignment:', error);
      setError('An error occurred while saving. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableUsers = (role) => {
    return availableUsers.filter(user => user.role === role);
  };

  const getUserById = (userId) => {
    let result = availableUsers.find(user => user.id === userId || user.email === userId);
    console.log(`Searching for user with ID/Email: ${userId}`, result);
    return result || null;
  };

  const usersList = getAvailableUsers('User');
  const leadsList = getAvailableUsers('Lead');
  const managersList = getAvailableUsers('Manager');

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
          mx: 'auto',
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha('#0a0068', 0.15)}`
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
        color: 'white',
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ 
            bgcolor: alpha('#ffffff', 0.2), 
            width: 48, 
            height: 48,
            boxShadow: `0 4px 12px ${alpha('#000000', 0.2)}`
          }}>
            <PersonAddIcon sx={{ fontSize: 24, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              fontSize: { xs: '1.1rem', md: '1.25rem' }, 
              mb: 0.5,
              letterSpacing: '0.5px'
            }}>
              User & Lead Assignment
            </Typography>
            <Typography variant="body2" sx={{ 
              opacity: 0.9, 
              fontSize: { xs: '0.8rem', md: '0.85rem' },
              fontWeight: 400
            }}>
              Manage user-lead relationships and reporting structure
            </Typography>
          </Box>
        </Stack>
        <Button
          onClick={onClose}
          sx={{ 
            color: 'white',
            minWidth: 'auto',
            p: 1,
            borderRadius: 2,
            '&:hover': { 
              bgcolor: alpha('#ffffff', 0.1),
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              fontSize: '0.875rem',
              borderRadius: 2,
              boxShadow: `0 2px 8px ${alpha('#f44336', 0.15)}`
            }}
          >
            {error}
          </Alert>
        )}

        <Card elevation={0} sx={{ 
          mb: 3,
          background: `linear-gradient(135deg, ${alpha('#0a0068', 0.02)} 0%, ${alpha('#cc4049', 0.02)} 100%)`,
          border: `1px solid ${alpha('#0a0068', 0.1)}`,
          borderRadius: 3
        }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Avatar sx={{ 
                bgcolor: alpha('#0a0068', 0.1),
                color: '#0a0068',
                width: 40,
                height: 40
              }}>
                <GroupIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  fontSize: '1rem',
                  color: '#0a0068',
                  mb: 0.5
                }}>
                  Assignment Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontSize: '0.8rem', 
                  lineHeight: 1.5
                }}>
                  Create user-lead relationships and define team structure
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={{ xs: 2, md: 3 }}>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={usersList}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={getUserById(hierarchyForm.user_id) || null}
              onChange={(e, newValue) => handleFormChange('user_id', newValue ? newValue : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign User"
                  required
                  fullWidth
                  variant="outlined"
                  helperText="Select the User to be assigned to the Lead"
                  InputLabelProps={{ 
                    sx: { 
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#0a0068'
                    } 
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { 
                      fontSize: '0.875rem',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#0a0068', 0.2),
                        borderWidth: 1.5
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#0a0068', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0a0068',
                        borderWidth: 2
                      }
                    }
                  }}
                  FormHelperTextProps={{ 
                    sx: { 
                      fontSize: '0.75rem',
                      mt: 1,
                      color: theme.palette.text.secondary
                    } 
                  }}
                />
              )}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.875rem'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={leadsList}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={getUserById(hierarchyForm.lead_id) || null}
              onChange={(e, newValue) => handleFormChange('lead_id', newValue ? newValue : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Lead"
                  required
                  fullWidth
                  variant="outlined"
                  helperText="Select the Lead who will manage this user"
                  InputLabelProps={{ 
                    sx: { 
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#cc4049'
                    } 
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { 
                      fontSize: '0.875rem',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#cc4049', 0.2),
                        borderWidth: 1.5
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#cc4049', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#cc4049',
                        borderWidth: 2
                      }
                    }
                  }}
                  FormHelperTextProps={{ 
                    sx: { 
                      fontSize: '0.75rem',
                      mt: 1,
                      color: theme.palette.text.secondary
                    } 
                  }}
                />
              )}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.875rem'
                  }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Team Name"
              value={hierarchyForm.team_name}
              onChange={(e) => handleFormChange('team_name', e.target.value)}
              required
              variant="outlined"
              helperText="Enter the team name for this assignment"
              InputLabelProps={{ 
                sx: { 
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: '#0a0068'
                } 
              }}
              InputProps={{ 
                sx: { 
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#0a0068', 0.2),
                    borderWidth: 1.5
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#0a0068', 0.4)
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0a0068',
                    borderWidth: 2
                  }
                }
              }}
              FormHelperTextProps={{ 
                sx: { 
                  fontSize: '0.75rem',
                  mt: 1,
                  color: theme.palette.text.secondary
                } 
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Department"
              value={hierarchyForm.department}
              onChange={(e) => handleFormChange('department', e.target.value)}
              variant="outlined"
              helperText="Enter the department (optional)"
              InputLabelProps={{ 
                sx: { 
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: '#cc4049'
                } 
              }}
              InputProps={{ 
                sx: { 
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#cc4049', 0.2),
                    borderWidth: 1.5
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#cc4049', 0.4)
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cc4049',
                    borderWidth: 2
                  }
                }
              }}
              FormHelperTextProps={{ 
                sx: { 
                  fontSize: '0.75rem',
                  mt: 1,
                  color: theme.palette.text.secondary
                } 
              }}
            />
          </Grid>

          {userRole === "Manager" && (
            <Grid item xs={12}>
              <Alert 
                severity="info" 
                sx={{ 
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  bgcolor: alpha('#0a0068', 0.05),
                  borderColor: alpha('#0a0068', 0.2),
                  '& .MuiAlert-icon': {
                    color: '#0a0068'
                  }
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BusinessIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.8rem',
                    color: '#0a0068',
                    fontWeight: 500
                  }}>
                    As a Manager, you are automatically set as the manager for this hierarchy.
                  </Typography>
                </Stack>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, md: 3 }, 
        gap: 2, 
        bgcolor: alpha('#f5f5f5', 0.5),
        borderTop: `1px solid ${alpha('#0a0068', 0.1)}`,
        justifyContent: { xs: 'stretch', sm: 'flex-end' },
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            textTransform: 'none', 
            fontSize: '0.875rem',
            fontWeight: 600,
            minWidth: { xs: 'auto', sm: 120 },
            borderRadius: 2,
            borderColor: alpha('#666666', 0.3),
            color: '#666666',
            '&:hover': {
              borderColor: alpha('#666666', 0.5),
              bgcolor: alpha('#666666', 0.05)
            }
          }}
        >
          Cancel
        </Button>
        <Tooltip 
          title={
            !isFormValid && !loading ? 
              `Please fill: ${getMissingFields().join(', ')}` : 
              ''
          }
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isFormValid}
              startIcon={!loading && <SaveIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                minWidth: { xs: 'auto', sm: 160 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                boxShadow: `0 4px 12px ${alpha('#0a0068', 0.3)}`,
                '&:hover': {
                  background: 'linear-gradient(135deg, #080056 0%, #b8363c 100%)',
                  boxShadow: `0 6px 16px ${alpha('#0a0068', 0.4)}`,
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  background: alpha('#666666', 0.2),
                  color: alpha('#ffffff', 0.5),
                  cursor: 'not-allowed',
                  boxShadow: 'none'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {loading 
                ? 'Saving...' 
                : !isFormValid 
                  ? 'Fill Required Fields'
                  : editingHierarchy 
                    ? 'Update Assignment' 
                    : 'Create Assignment'
              }
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default UserHierarchyDialog;